import React from "react";
import { Snowflake, MapPin, Calendar, Users, Mail, Phone, Check, Sparkles, Printer, Share2, Bed, Plane } from "lucide-react";
import { cities, tripFacts, included, guide, HERO_IMAGE } from "../mock";
import "./flyer.css";

const SITE_URL = "https://beyondthepathtravel.com/christmas";

const Flyer = () => {
  const handlePrint = () => window.print();
  const shareMail = `mailto:?subject=Europe's Christmas Markets — Ladies' Trip Dec 3-13, 2026&body=Hi! I wanted to share this gorgeous Christmas Markets trip with you — full details, itinerary and registration here: ${SITE_URL} %0D%0A%0D%0AHosted by Sophia Borkgren · @beyondthepath_`;

  return (
    <div className="flyer-root font-body bg-[#e6dfd0] min-h-screen py-8">
      {/* Toolbar — hidden when printing */}
      <div className="no-print max-w-[8.5in] mx-auto px-4 mb-5 flex flex-wrap items-center justify-between gap-3">
        <a href="/" className="text-[12px] tracking-[0.2em] uppercase text-[#1a3a2e] hover:text-[#c9a55c] transition-colors">
          ← Back to site
        </a>
        <div className="flex gap-3">
          <button onClick={handlePrint} className="btn-primary !py-2.5 !px-5 text-[11px]">
            <Printer className="w-4 h-4" strokeWidth={1.5} />
            Print / Save as PDF
          </button>
          <a href={shareMail} className="btn-ghost-dark !py-2.5 !px-5 text-[11px]">
            <Share2 className="w-4 h-4" strokeWidth={1.5} />
            Email This Flyer
          </a>
        </div>
      </div>

      {/* Flyer Page — Letter size 8.5x11" */}
      <div className="flyer-page mx-auto bg-[#faf5ec] shadow-2xl">
        {/* HERO */}
        <div
          className="flyer-hero relative overflow-hidden"
          style={{
            backgroundImage: `linear-gradient(rgba(12,26,20,0.55), rgba(12,26,20,0.75)), url(${HERO_IMAGE})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="flex items-center gap-2 absolute top-5 left-6">
            <Snowflake className="w-4 h-4 text-[#e9c987]" strokeWidth={1.5} />
            <div className="leading-none">
              <div className="font-display text-[#faf5ec] text-[15px] tracking-wide">Beyond the Path</div>
              <div className="font-script text-[#e9c987] text-[13px] -mt-0.5">travel</div>
            </div>
          </div>
          <div className="absolute top-5 right-6 text-right">
            <div className="text-[9px] tracking-[0.3em] uppercase text-[#e9c987]">Ladies' Holiday Trip</div>
            <div className="text-[10px] text-[#faf5ec]/80 mt-0.5">December 3 – 13, 2026</div>
          </div>

          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-8 pt-8">
            <div className="flex items-center gap-2 text-[#e9c987] mb-2">
              <span className="h-px w-6 bg-[#e9c987]" />
              <span className="text-[9px] tracking-[0.4em] uppercase">A 10-Day Ladies' Getaway</span>
              <span className="h-px w-6 bg-[#e9c987]" />
            </div>
            <h1 className="font-display text-[#faf5ec] text-[38px] leading-[1.05] max-w-[6in]">
              Experience the Magic of
              <span className="font-script text-[#e9c987] text-[54px] block leading-[1.05]">
                Europe's Christmas
              </span>
              <span className="font-display italic">Markets</span>
            </h1>
            <p className="mt-4 text-[#faf5ec]/90 text-[12px] max-w-[5in] leading-relaxed">
              London · Brussels · Bruges · Paris · Strasbourg — five magical cities,
              mulled wine, twinkling lights and shared memories that sparkle long after the season ends.
            </p>
          </div>

          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-6 text-[#faf5ec]/90 text-[10px]">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="w-3 h-3 text-[#e9c987]" strokeWidth={1.5} />
              10 Days / 9 Nights
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="w-3 h-3 text-[#e9c987]" strokeWidth={1.5} />
              UK · Belgium · France
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Users className="w-3 h-3 text-[#e9c987]" strokeWidth={1.5} />
              Small Group
            </span>
          </div>
        </div>

        {/* CITIES STRIP */}
        <div className="px-6 pt-5 pb-3">
          <div className="text-center mb-3">
            <span className="font-script text-[#c9a55c] text-xl block leading-none">the route</span>
            <h2 className="font-display text-[#1a3a2e] text-[20px] leading-tight">
              Five Cities, One <em className="italic text-[#c9a55c]">Festive</em> Story
            </h2>
          </div>
          <div className="grid grid-cols-5 gap-2">
            {cities.map((c) => (
              <div key={c.name} className="relative h-[105px] rounded-sm overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${c.image})` }} />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0c1a14]/95 via-[#0c1a14]/20 to-transparent" />
                <div className="absolute bottom-1.5 left-2 right-2">
                  <div className="text-[7px] tracking-[0.25em] uppercase text-[#e9c987] font-medium">
                    {c.country}
                  </div>
                  <div className="font-display text-[#faf5ec] text-[15px] leading-none">{c.name}</div>
                </div>
                <div className="absolute top-1 right-1 px-1.5 py-0.5 bg-[#c9a55c] text-[#1a3a2e] text-[7px] tracking-[0.2em] uppercase font-semibold rounded-sm">
                  {c.days}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* MIDDLE CONTENT: Included + Price */}
        <div className="px-6 pt-3 pb-4 grid grid-cols-3 gap-4">
          {/* What's Included */}
          <div className="col-span-2 bg-[#fdfaf2] border border-[#1a3a2e]/10 p-4 rounded-sm">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-[#c9a55c]" strokeWidth={1.5} />
              <h3 className="font-display text-[#1a3a2e] text-[16px]">What's Included</h3>
            </div>
            <ul className="grid grid-cols-2 gap-x-3 gap-y-1.5">
              {included.map((it, i) => (
                <li key={i} className="flex items-start gap-1.5 text-[#3b463f] text-[10.5px] leading-snug font-light">
                  <Check className="w-3 h-3 text-[#c9a55c] mt-0.5 shrink-0" strokeWidth={2} />
                  <span>{it}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 pt-2.5 border-t border-[#1a3a2e]/10 grid grid-cols-3 gap-2 text-[9px]">
              <div>
                <div className="text-[#3b463f]/60 uppercase tracking-[0.2em]">Begins</div>
                <div className="font-display text-[#1a3a2e] text-[12px]">London, UK</div>
              </div>
              <div>
                <div className="text-[#3b463f]/60 uppercase tracking-[0.2em]">Ends</div>
                <div className="font-display text-[#1a3a2e] text-[12px]">Paris, France</div>
              </div>
              <div>
                <div className="text-[#3b463f]/60 uppercase tracking-[0.2em]">Hotels</div>
                <div className="font-display text-[#1a3a2e] text-[12px]">3–4★, 9 Nights</div>
              </div>
            </div>
          </div>

          {/* Price card */}
          <div className="bg-[#1a3a2e] text-[#faf5ec] p-4 rounded-sm relative overflow-hidden">
            <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full bg-[#c9a55c]/15 blur-2xl" />
            <div className="relative">
              <div className="text-[8px] tracking-[0.3em] uppercase text-[#e9c987]">Trip Cost</div>
              <div className="mt-0.5 flex items-baseline gap-1">
                <span className="font-display text-[36px] leading-none">{tripFacts.price}</span>
                <span className="text-[#faf5ec]/60 text-[10px]">/ person</span>
              </div>
              <p className="text-[#faf5ec]/70 text-[9px] mt-1 font-light leading-snug">
                Based on double occupancy. Airfare not included (~$1,000 est.)
              </p>
              <div className="my-2.5 h-px bg-[#faf5ec]/15" />
              <ul className="space-y-1 text-[9.5px] font-light leading-snug">
                <li className="flex gap-1.5">
                  <span className="text-[#e9c987] mt-0.5">•</span>
                  <span>
                    <strong className="text-[#e9c987] font-medium">$200</strong> non-refundable registration fee (separate from trip cost)
                  </span>
                </li>
                <li className="flex gap-1.5">
                  <span className="text-[#e9c987] mt-0.5">•</span>
                  <span>Full $2,695 balance due by <strong className="text-[#e9c987] font-medium">August 29, 2026</strong></span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* BOTTOM: Guide + CTA */}
        <div className="px-6 pb-5 grid grid-cols-3 gap-4">
          {/* Guide card */}
          <div className="col-span-2 bg-[#fdfaf2] border border-[#1a3a2e]/10 p-3 rounded-sm flex gap-3">
            <div
              className="w-[90px] h-[120px] bg-cover bg-center rounded-sm shrink-0"
              style={{ backgroundImage: `url(${guide.image})` }}
            />
            <div className="flex-1">
              <div className="text-[8px] tracking-[0.3em] uppercase text-[#c9a55c]">Meet Your Guide</div>
              <h3 className="font-display text-[#1a3a2e] text-[17px] leading-tight mt-0.5">
                Hi, I'm <em className="italic">Sophia</em>!
              </h3>
              <p className="text-[#3b463f] text-[10.5px] font-light leading-snug mt-1.5">
                After years of leading group travel and falling in love with Central Europe's winter magic,
                I created this trip to share the festive markets, cozy vibes and snowy charm with an
                incredible group of women. Let's make memories that sparkle as bright as the Christmas lights!
              </p>
              <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
                <span className="inline-flex items-center gap-1 text-[#1a3a2e]">
                  <Mail className="w-3 h-3 text-[#c9a55c]" strokeWidth={1.5} />
                  {guide.email}
                </span>
                <span className="inline-flex items-center gap-1 text-[#1a3a2e]">
                  <Phone className="w-3 h-3 text-[#c9a55c]" strokeWidth={1.5} />
                  {guide.phone}
                </span>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-[#c9a55c] text-[#1a3a2e] p-4 rounded-sm flex flex-col justify-center text-center">
            <span className="font-script text-[#1a3a2e]/80 text-[20px] leading-none">join us</span>
            <h3 className="font-display text-[22px] leading-tight mt-0.5">
              Reserve Your <em className="italic">Spot</em>
            </h3>
            <p className="text-[#1a3a2e]/80 text-[9.5px] font-light leading-snug mt-2">
              Spots are limited — small group trip. Lock yours in with the $200 registration fee.
            </p>
            <a
              href={`mailto:${guide.email}?subject=Christmas Markets Trip — Reserve My Spot&body=Hi Sophia! I'd love to reserve my spot on the Europe's Christmas Markets trip (Dec 3-13, 2026). Please send me next steps for the $200 registration fee.%0D%0A%0D%0AName:%0D%0APhone:%0D%0ARoommate preference:%0D%0AAnything else:`}
              className="mt-2.5 inline-block bg-[#1a3a2e] text-[#faf5ec] px-3 py-2 text-[9px] tracking-[0.2em] uppercase font-medium rounded-sm hover:bg-[#0f261e] transition-colors no-underline"
            >
              Email Sophia to Register
            </a>
          </div>
        </div>

        {/* FOOTER */}
        <div className="px-6 py-2.5 bg-[#0f261e] text-[#faf5ec]/80 flex items-center justify-between text-[8.5px]">
          <div className="flex items-center gap-1.5">
            <Snowflake className="w-3 h-3 text-[#e9c987]" strokeWidth={1.5} />
            <span className="font-display text-[10px] text-[#faf5ec]">Beyond the Path</span>
            <span className="font-script text-[#e9c987] text-[10px]">travel</span>
          </div>
          <div className="font-script text-[#e9c987] text-[11px]">wandering, together.</div>
          <div>© 2026 · All journeys reserved.</div>
        </div>
      </div>
    </div>
  );
};

export default Flyer;
